import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, Spinner } from "reactstrap";
import axios from "axios";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { abrirPDF } from "../../../../utils/abrirPDF";

// Estado de cuenta POR NOTA (estilo Macuro): cada venta / nota de cargo con el detalle de los
// pagos que se le aplicaron. Cargos en negro, abonos/bonificaciones/devoluciones en ROJO y
// saldo en AZUL; las notas vencidas (según el plazo del cliente) se marcan en rojo claro.
const URL_ESTADO_CUENTA = process.env.REACT_APP_URL_ESTADO_CUENTA;

const ROJO = "#d40000";
const AZUL = "#0b3fd6";
const VENCIDO_BG = "#f8d0d0";
const fmt = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(Number(v || 0));
const fmtSiHay = (v) => (Number(v) > 0 ? fmt(v) : "");
const fechaMx = (f) => (f ? String(f).split("-").reverse().join("/") : "");

function EstadoCuentaPorNota({ isOpen, toggle, cliente, onAbono, recarga = 0 }) {
  const [cargando, setCargando] = useState(false);
  const [grupos, setGrupos] = useState([]);
  const [plazo, setPlazo] = useState(0);
  const [soloConSaldo, setSoloConSaldo] = useState(true);

  useEffect(() => {
    if (!isOpen || !cliente) return;
    // Limpia lo del cliente anterior mientras carga (si no, se ven sus totales y su plazo)
    setGrupos([]);
    setPlazo(0);
    setCargando(true);
    axios
      .get(`${URL_ESTADO_CUENTA}PorNota/${cliente.id}`, {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      })
      .then((r) => {
        setGrupos((r.data && r.data.grupos) || []);
        setPlazo((r.data && r.data.plazo) || 0);
      })
      .catch(() => {
        setGrupos([]);
        setPlazo(0);
      })
      .finally(() => setCargando(false));
  }, [isOpen, cliente && cliente.id, recarga]); // eslint-disable-line react-hooks/exhaustive-deps

  // Con saldo = debe algo o tiene saldo a favor (devolución = nota en negativo)
  const visibles = grupos.filter((g) => !soloConSaldo || Math.abs(g.saldo) > 0.01);
  const pagado = (g) => g.movimientos.reduce((t, m) => t + m.abono + m.bonificacion + m.devolucion, 0);
  const tot = visibles.reduce(
    (t, g) => ({
      cargos: t.cargos + g.cargo,
      pagos: t.pagos + pagado(g),
      saldo: t.saldo + g.saldo,
      vencido: t.vencido + (g.vencido ? g.saldo : 0),
    }),
    { cargos: 0, pagos: 0, saldo: 0, vencido: 0 }
  );

  // Renglones de una nota: la nota (cargo) y luego cada pago con su saldo corrido
  function renglones(g) {
    let corrido = g.cargo;
    const filas = [
      {
        clave: "nota",
        numero: g.numero,
        fecha: g.fecha,
        movimiento: g.tipo,
        detalle: [g.factura ? `Factura ${g.factura}` : "", g.observaciones].filter(Boolean).join(" · "),
        cargo: g.cargo,
        abono: 0,
        bonificacion: 0,
        devolucion: 0,
        saldo: corrido,
      },
    ];
    g.movimientos.forEach((m, i) => {
      corrido -= m.abono + m.bonificacion + m.devolucion;
      filas.push({
        clave: `m${i}`,
        numero: m.numero,
        fecha: m.fecha,
        movimiento: m.movimiento,
        detalle: [m.formaPago, m.banco, m.observaciones].filter(Boolean).join(" · "),
        cargo: 0,
        abono: m.abono,
        bonificacion: m.bonificacion,
        devolucion: m.devolucion,
        saldo: corrido,
      });
    });
    return filas;
  }

  function pdf() {
    const doc = new jsPDF("landscape");
    const img = new Image();
    img.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img, "png", 250, 6, 30, 30);
    doc.setFontSize(16);
    doc.text("Estado de Cuenta por Nota", 14, 16);
    doc.setFontSize(11);
    doc.text(`Cliente: ${cliente.nombre}`, 14, 24);
    doc.text(`Plazo: ${plazo} días    Fecha: ${new Date().toLocaleDateString("es-MX")}`, 14, 30);
    doc.setFontSize(9);
    doc.setTextColor(ROJO);
    doc.text("Pagos en rojo", 14, 36);
    doc.setTextColor(AZUL);
    doc.text("Saldo en azul", 40, 36);
    doc.setTextColor("#000000");

    let y = 40;
    const head = [["NUM.", "FECHA", "MOVIMIENTO", "DETALLE", "CARGO", "ABONO", "BONIF.", "DEVOL.", "SALDO"]];
    visibles.forEach((g) => {
      const filas = renglones(g).map((f) => [
        f.numero,
        fechaMx(f.fecha),
        f.movimiento,
        f.detalle,
        fmtSiHay(f.cargo),
        fmtSiHay(f.abono),
        fmtSiHay(f.bonificacion),
        fmtSiHay(f.devolucion),
        fmt(f.saldo),
      ]);
      doc.autoTable({
        head,
        body: filas,
        foot: [
          [
            "",
            "",
            "",
            { content: `Saldo ${g.tipo.toLowerCase()} #${g.numero}${g.vencido ? ` (vencida ${g.diasVencido} días)` : ""}`, colSpan: 5, styles: { halign: "right" } },
            { content: fmt(g.saldo), styles: { halign: "right" } },
          ],
        ],
        startY: y,
        theme: "grid",
        margin: { left: 8, right: 8 },
        headStyles: { fillColor: g.vencido ? [237, 140, 140] : [15, 23, 42], textColor: g.vencido ? 0 : 255, fontSize: 8, halign: "center" },
        footStyles: { fillColor: [127, 127, 127], textColor: 255, fontSize: 8, fontStyle: "bold" },
        styles: { fontSize: 8 },
        columnStyles: {
          0: { halign: "center", cellWidth: 16 },
          1: { halign: "center", cellWidth: 22 },
          2: { cellWidth: 28 },
          4: { halign: "right", cellWidth: 26 },
          5: { halign: "right", textColor: ROJO, cellWidth: 26 },
          6: { halign: "right", textColor: ROJO, cellWidth: 24 },
          7: { halign: "right", textColor: ROJO, cellWidth: 24 },
          8: { halign: "right", textColor: AZUL, cellWidth: 28, fontStyle: "bold" },
        },
        didParseCell: (data) => {
          if (data.section === "body" && data.row.index === 0) {
            data.cell.styles.fontStyle = "bold";
            if (g.vencido) data.cell.styles.fillColor = [248, 208, 208];
          }
        },
      });
      y = doc.lastAutoTable.finalY + 4;
    });

    doc.autoTable({
      startY: y + 2,
      theme: "grid",
      margin: { left: 160, right: 8 },
      body: [
        ["Total cargos", fmt(tot.cargos)],
        [{ content: "Total pagos", styles: { textColor: ROJO } }, { content: fmt(tot.pagos), styles: { textColor: ROJO } }],
        [{ content: "Saldo vencido", styles: { textColor: ROJO } }, { content: fmt(tot.vencido), styles: { textColor: ROJO } }],
        [{ content: "Saldo", styles: { textColor: AZUL } }, { content: fmt(tot.saldo), styles: { textColor: AZUL } }],
      ],
      styles: { fontSize: 10, fontStyle: "bold" },
      columnStyles: { 1: { halign: "right" } },
    });
    abrirPDF(doc);
  }

  if (!cliente) return null;
  const th = { padding: "6px 8px", fontSize: 11, textTransform: "uppercase", whiteSpace: "nowrap" };
  const td = { padding: "5px 8px", fontSize: 13, borderTop: "1px solid #e2e8f0" };
  const num = { ...td, textAlign: "right", whiteSpace: "nowrap" };

  return (
    <Modal size="xl" isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        <i className="fas fa-receipt" style={{ color: ROJO, marginRight: 8 }} />
        Estado de cuenta por nota — {cliente.nombre}
        <div style={{ fontSize: 13, fontWeight: 400 }}>
          Plazo {plazo} días · <span style={{ color: ROJO, fontWeight: 600 }}>pagos en rojo</span> ·{" "}
          <span style={{ color: AZUL, fontWeight: 600 }}>saldo en azul</span>
        </div>
      </ModalHeader>
      <ModalBody>
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
          <Label check style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 20 }}>
            <Input type="checkbox" checked={soloConSaldo} onChange={(e) => setSoloConSaldo(e.target.checked)} />
            Solo notas con saldo
          </Label>
          <div style={{ marginLeft: "auto", display: "flex", gap: 18, flexWrap: "wrap", fontSize: 14 }}>
            <span>
              Cargos <b>{fmt(tot.cargos)}</b>
            </span>
            <span style={{ color: ROJO }}>
              Pagos <b>{fmt(tot.pagos)}</b>
            </span>
            <span style={{ color: ROJO }}>
              Vencido <b>{fmt(tot.vencido)}</b>
            </span>
            <span style={{ color: AZUL }}>
              Saldo <b>{fmt(tot.saldo)}</b>
            </span>
          </div>
        </div>

        {cargando ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spinner />
          </div>
        ) : !visibles.length ? (
          <h5 style={{ textAlign: "center", padding: 30 }}>{soloConSaldo ? "Este cliente no tiene notas con saldo." : "Este cliente no tiene notas."}</h5>
        ) : (
          visibles.map((g) => (
            <div key={g._id} style={{ border: `1px solid ${g.vencido ? "#ef9a9a" : "#e2e8f0"}`, borderRadius: 10, marginBottom: 12, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: g.vencido ? VENCIDO_BG : "#0f172a", color: g.vencido ? "#7f1d1d" : "#fff" }}>
                      <th style={th}>Núm.</th>
                      <th style={th}>Fecha</th>
                      <th style={th}>Movimiento</th>
                      <th style={th}>Detalle</th>
                      <th style={{ ...th, textAlign: "right" }}>Cargo</th>
                      <th style={{ ...th, textAlign: "right" }}>Abono</th>
                      <th style={{ ...th, textAlign: "right" }}>Bonif.</th>
                      <th style={{ ...th, textAlign: "right" }}>Devol.</th>
                      <th style={{ ...th, textAlign: "right" }}>Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renglones(g).map((f) => {
                      const esNota = f.clave === "nota";
                      return (
                        <tr key={f.clave} style={{ background: esNota && g.vencido ? "#fdecec" : undefined, fontWeight: esNota ? 700 : 400 }}>
                          <td style={td}>{f.numero}</td>
                          <td style={{ ...td, whiteSpace: "nowrap" }}>{fechaMx(f.fecha)}</td>
                          <td style={td}>{esNota ? f.movimiento : `↳ ${f.movimiento}`}</td>
                          <td style={{ ...td, color: "#64748b", fontWeight: 400 }}>{f.detalle}</td>
                          <td style={num}>{fmtSiHay(f.cargo)}</td>
                          <td style={{ ...num, color: ROJO }}>{fmtSiHay(f.abono)}</td>
                          <td style={{ ...num, color: ROJO }}>{fmtSiHay(f.bonificacion)}</td>
                          <td style={{ ...num, color: ROJO }}>{fmtSiHay(f.devolucion)}</td>
                          <td style={{ ...num, color: AZUL, fontWeight: 700 }}>{fmt(f.saldo)}</td>
                        </tr>
                      );
                    })}
                    <tr style={{ background: "#f1f5f9" }}>
                      <td style={{ ...td, fontWeight: 700 }} colSpan={4}>
                        {g.movimientos.length ? `${g.movimientos.length} pago(s) aplicados` : "Sin pagos"}
                        {g.vencido ? <span style={{ color: ROJO, marginLeft: 10 }}>VENCIDA hace {g.diasVencido} días</span> : null}
                        {g.saldo < -0.01 ? <span style={{ color: "#15803d", marginLeft: 10 }}>SALDO A FAVOR</span> : null}
                        {!g.vencido && Math.abs(g.saldo) <= 0.01 ? <span style={{ color: "#15803d", marginLeft: 10 }}>PAGADA ✓</span> : null}
                      </td>
                      <td style={{ ...num, fontWeight: 700 }} colSpan={4}>
                        Saldo {g.tipo.toLowerCase()} #{g.numero}
                      </td>
                      <td style={{ ...num, color: AZUL, fontWeight: 800 }}>{fmt(g.saldo)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </ModalBody>
      <ModalFooter>
        {onAbono ? (
          <Button color="warning" onClick={() => onAbono(cliente.id)}>
            <i className="fas fa-dollar-sign" /> Registrar abono
          </Button>
        ) : null}
        <Button color="danger" onClick={pdf} disabled={!visibles.length}>
          <i className="far fa-file-pdf" /> PDF
        </Button>
        <Button color="secondary" onClick={toggle}>
          Cerrar
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default EstadoCuentaPorNota;
